import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import BillingConfigForm from './BillingConfigForm'
import { BillingConfigFormValues, billingConfigSchema, configToFormValues, formValuesToPayload } from './schema'

import styles from './BillingConfigEditor.module.scss'

import { isApiError } from '@/api'
import { EnterpriseBillingConfigResponse } from '@/api/generated'
import { updateBillingConfig } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import Input from '@/components/Input'
import { captureEvent } from '@/utils/captureEvent'
import { EventName } from '@/utils/eventDefinitions'

interface EditBillingConfigDialogProps {
  isOpen: boolean
  config: EnterpriseBillingConfigResponse | null
  onSave: () => void
  onCancel: () => void
}

const EditBillingConfigDialog = ({ isOpen, config, onSave, onCancel }: EditBillingConfigDialogProps) => {
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const updateMutation = useMutation({
    mutationFn: (data: ReturnType<typeof formValuesToPayload>) => {
      if (!config) throw new Error('No config to update')
      return updateBillingConfig(config.id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billingConfigs'] })
      if (config) {
        captureEvent(EventName.BillingConfigEdited, {
          config_id: config.id,
          enterprise_name: config.enterprise_name,
        })
      }
      reset()
      onSave()
    },
    onError: (err) => {
      console.error('Failed to update billing config:', err)
      if (isApiError(err)) {
        let detail: string
        try {
          detail = typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail)
        } catch {
          detail = 'Failed to update billing config'
        }
        setError(detail || 'Failed to update billing config')
      } else {
        setError('Failed to update billing config')
      }
    },
  })

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<BillingConfigFormValues>({
    resolver: zodResolver(billingConfigSchema),
    defaultValues: config ? configToFormValues(config) : undefined,
  })

  // Reset form when config changes
  useEffect(() => {
    if (config) {
      reset(configToFormValues(config))
    }
  }, [config, reset])

  const onInvalid = () => {
    setError('Please fix the highlighted form errors')
  }

  const onSubmit = (values: BillingConfigFormValues) => {
    setError(null)
    updateMutation.mutate(formValuesToPayload(values))
  }

  const handleCancel = () => {
    setError(null)
    reset()
    onCancel()
  }

  return (
    <DialogWrapper isOpen={isOpen} title="Edit Billing Config" close={handleCancel} minWidth={640}>
      {config && (
        <form
          className={styles.dialogBody}
          onSubmit={handleSubmit(onSubmit, onInvalid)}
        >
          {error && <div className={styles.errorBanner}>{error}</div>}

          {/* Enterprise name (read-only UX to be more intentional with editing/deleting enterprise configs) */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Enterprise</label>
            <Input type="text" value={config.enterprise_name} className={styles.enterpriseInput} isDisabled />
            <span className={styles.hint}>ID: {config.enterprise_id}</span>
          </div>
          <BillingConfigForm control={control} errors={errors} disabled={updateMutation.isPending} setValue={setValue} />

          <div className={styles.dialogFooter}>
            <Button title="Cancel" onClick={handleCancel} type="light" />
            <Button
              title={updateMutation.isPending ? 'Saving...' : 'Save'}
              onClick={handleSubmit(onSubmit, onInvalid)}
              type="dark"
              isDisabled={updateMutation.isPending}
            />
          </div>
        </form>
      )}
    </DialogWrapper>
  )
}

export default EditBillingConfigDialog
